import {
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import MultiSelectDropdown from "../../../../../components/multi-select-dropdown/multi-select-dropdown";
import { useRequesterContactsSync } from "../../../contacts/use-requester-contacts-sync";

interface ExternalContact {
  fullName: string;
  email: string;
}

type Props = {
  formik: any;
  clientAllUsers: any;
  disabled?: boolean;
};

const Contacts = (props: Props) => {
  const { formik, clientAllUsers, disabled = false } = props;

  // Use formik values directly to avoid state synchronization issues
  const showVisionUsers = formik.values.contacts.showVisionUsers;
  const showExtraContacts = formik.values.contacts.showExtraContacts;

  const { clientUserOptions } = useRequesterContactsSync(
    formik,
    clientAllUsers
  );

  const handleVisionUsersChange = (checked: boolean) => {
    formik.setFieldValue("contacts.showVisionUsers", checked);
    if (!checked) {
      formik.setFieldValue("contacts.contactIds", []);
      formik.setFieldTouched("contacts.contactIds", false, false);
    }
  };

  const handleExtraContactsChange = (checked: boolean) => {
    formik.setFieldValue("contacts.showExtraContacts", checked);
    if (!checked) {
      formik.setFieldValue("contacts.externalContacts", [
        { fullName: "", email: "" },
      ]);
    } else {
      // When enabling external contacts, trigger validation
      setTimeout(() => {
        formik.setFieldTouched("contacts.externalContacts", true);
        formik.validateField("contacts.externalContacts");
      }, 100);
    }
  };

  const addExternalContact = () => {
    const currentContacts = formik.values.contacts.externalContacts;
    if (currentContacts.length < 5) {
      const newContacts = [...currentContacts, { fullName: "", email: "" }];
      formik.setFieldValue("contacts.externalContacts", newContacts);
      formik.setFieldTouched("contacts.externalContacts", true);

      setTimeout(() => {
        formik.validateField("contacts.externalContacts");
      }, 0);
    }
  };

  const removeExternalContact = (index: number) => {
    const currentContacts = formik.values.contacts.externalContacts;
    const newContacts = currentContacts.filter(
      (_: any, i: number) => i !== index
    );
    formik.setFieldValue("contacts.externalContacts", newContacts);
    formik.setFieldTouched("contacts.externalContacts", true);
  };

  const updateExternalContact = (
    index: number,
    field: keyof ExternalContact,
    value: string
  ) => {
    const currentContacts = [...formik.values.contacts.externalContacts];
    currentContacts[index][field] = value;
    formik.setFieldValue("contacts.externalContacts", currentContacts);
    formik.setFieldTouched("contacts.externalContacts", true);
    formik.setFieldTouched(`contacts.externalContacts.${index}.${field}`, true);

    setTimeout(() => {
      formik.validateField("contacts.externalContacts");
    }, 0);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
      }}
    >
      <FormControlLabel
        control={
          <Checkbox
            checked={formik.values.contacts.includeRequester}
            onChange={(e) => {
              formik.setFieldValue(
                "contacts.includeRequester",
                e.target.checked
              );
              formik.setFieldTouched("contacts.includeRequester", true);
            }}
            name="includeRequester"
            disabled={!formik.values.clientId || disabled}
          />
        }
        label="Requestor"
      />
      {formik.values.contacts.includeRequester && formik.values.requesterId && (
        <Typography
          sx={{
            padding: "3px 14px",
            border: "1px solid",
            borderColor: "primary.main",
            borderRadius: "100px",
            color: "primary.main",
            display: "inline-block",
            width: "fit-content",
            margin: 1,
            ml: 4,
          }}
        >
          {
            clientAllUsers?.filter(
              (client: any) => client.id === formik.values.requesterId
            )?.[0]?.fullName
          }
        </Typography>
      )}
      <FormControlLabel
        control={
          <Checkbox
            checked={showVisionUsers}
            onChange={(e) => handleVisionUsersChange(e.target.checked)}
            name="visionUsers"
            disabled={!formik.values.clientId || disabled}
          />
        }
        label="Client User(s)"
      />
      {showVisionUsers && (
        <Box sx={{ ml: 4, mt: 1 }}>
          <MultiSelectDropdown
            label=""
            options={clientUserOptions}
            selectedValues={formik.values.contacts.contactIds}
            onChange={(selected) => {
              formik.setFieldTouched("contacts.contactIds", true, false);
              formik.setFieldValue("contacts.contactIds", selected, true);
            }}
            placeholder="Search and select client users..."
            width="100%"
            maxHeight={200}
            // disabled={disabled}
          />
          {formik.touched.contacts?.contactIds &&
            formik.errors.contacts?.contactIds && (
              <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                {String(formik.errors.contacts.contactIds) ||
                  "This field can't be empty"}
              </Typography>
            )}
        </Box>
      )}
      <FormControlLabel
        control={
          <Checkbox
            checked={showExtraContacts}
            onChange={(e) => handleExtraContactsChange(e.target.checked)}
            name="externalContacts"
            disabled={!formik.values.clientId || disabled}
          />
        }
        label="External Contact(s)"
      />
      {showExtraContacts && (
        <Box sx={{ ml: 4, mt: 1 }}>
          {formik.values.contacts.externalContacts.map(
            (contact: ExternalContact, index: number) => (
              <Box
                key={index}
                sx={{ display: "flex", gap: 2, mb: 2, alignItems: "start" }}
              >
                <TextField
                  label="Name"
                  value={contact.fullName}
                  onChange={(e) =>
                    updateExternalContact(index, "fullName", e.target.value)
                  }
                  onBlur={() =>
                    formik.setFieldTouched(
                      `contacts.externalContacts.${index}.fullName`,
                      true
                    )
                  }
                  size="small"
                  sx={{ minWidth: "348px", width: "500px" }}
                  error={
                    !!(formik.errors.contacts?.externalContacts?.[index] as any)
                      ?.fullName &&
                    !!(
                      formik.touched.contacts?.externalContacts?.[index] as any
                    )?.fullName
                  }
                  helperText={
                    (formik.touched.contacts?.externalContacts?.[index] as any)
                      ?.fullName &&
                    (formik.errors.contacts?.externalContacts?.[index] as any)
                      ?.fullName
                      ? String(
                          (
                            formik.errors.contacts?.externalContacts?.[
                              index
                            ] as any
                          )?.fullName || ""
                        )
                      : ""
                  }
                  disabled={!formik.values.clientId || disabled}
                />
                <TextField
                  label="Email"
                  value={contact.email}
                  onChange={(e) =>
                    updateExternalContact(index, "email", e.target.value)
                  }
                  onBlur={() =>
                    formik.setFieldTouched(
                      `contacts.externalContacts.${index}.email`,
                      true
                    )
                  }
                  size="small"
                  sx={{ minWidth: "348px", width: "500px" }}
                  error={
                    !!(formik.errors.contacts?.externalContacts?.[index] as any)
                      ?.email &&
                    !!(
                      formik.touched.contacts?.externalContacts?.[index] as any
                    )?.email
                  }
                  helperText={
                    (formik.touched.contacts?.externalContacts?.[index] as any)
                      ?.email &&
                    (formik.errors.contacts?.externalContacts?.[index] as any)
                      ?.email
                      ? String(
                          (
                            formik.errors.contacts?.externalContacts?.[
                              index
                            ] as any
                          )?.email || ""
                        )
                      : ""
                  }
                  disabled={!formik.values.clientId || disabled}
                />
                <IconButton
                  onClick={() => removeExternalContact(index)}
                  size="small"
                  sx={{ color: "error.main" }}
                  disabled={!formik.values.clientId || disabled}
                >
                  <DeleteOutlineIcon />
                </IconButton>
              </Box>
            )
          )}
          {formik.values.contacts.externalContacts.length < 5 && (
            <Button
              startIcon={<AddIcon />}
              onClick={addExternalContact}
              sx={{
                color: "primary.main",
                textTransform: "none",
                fontSize: "14px",
                p: 0,
                minWidth: "auto",
                "&:hover": {
                  backgroundColor: "transparent",
                  textDecoration: "none",
                },
              }}
              disabled={!formik.values.clientId || disabled}
            >
              ADD ADDITIONAL CONTACT
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

export default Contacts;
